import { zodResolver } from '@hookform/resolvers/zod';
import { DB_TYPE } from '@quesmed/types';
import { EBatchType } from '@quesmed/types/models';
import {
  CLONE_CONTENT,
  type ICloneContentData,
  type ICloneContentVar,
} from '@quesmed/types/resolvers/mutation/admin';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import { batchTypes, dbTypes } from '@/config/constants';
import { ErrorFeedback, Heading, SuccessDataFeedback } from '@/core/molecules';
import {
  Alert,
  AnimatedComponent,
  Button,
  cn,
  FormFileInput,
  FormSelect,
  type FormSelectProps,
  type ISelectItem,
  toaster,
} from '@/core/ui';
import { useMutation } from '@/libs';
import { useGlobal } from '@/store';
import { localFileDownload } from '@/utils/dom';
import { fileSchema } from '@/utils/schema';

const CloneController = ({ className, ...rest }: React.ComponentProps<'div'>) => {
  const schema = z.object({
    type: z.enum(EBatchType, { message: 'Invalid Product value' }),
    source: z.enum(DB_TYPE, { message: 'Invalid Product value' }),
    destination: z.enum(DB_TYPE, { message: 'Invalid Product value' }),
    file: fileSchema,
  });

  type ISchema = z.infer<typeof schema>;

  const {
    control,
    handleSubmit,
    resetField,
    formState: { isValid },
  } = useForm<ISchema>({
    mode: 'onChange',
    resolver: zodResolver(schema),
  });
  const [destinationTypes, setDestinationTypes] = useState<ISelectItem[]>([]);

  const [cloneContent, { loading, data }] = useMutation<ICloneContentData, ICloneContentVar>(CLONE_CONTENT);
  const setDbType = useGlobal.use.setDbType();
  const finalData = useMemo(() => data?.admin?.cloneContent, [data?.admin?.cloneContent]);
  useEffect(() => {
    setDbType(DB_TYPE.FINALS);
    return () => {
      setDbType(DB_TYPE.FINALS);
    };
  }, []);

  const onSubmit: SubmitHandler<ISchema> = async (input) => {
    try {
      await cloneContent({ variables: input });
    } finally {
      resetField('file');
    }
  };

  const handleSourceChange: FormSelectProps<ISchema>['onValueChange'] = (value) => {
    setDestinationTypes(dbTypes.filter((p) => p.key !== value));
  };

  return (
    <div className={cn('flex flex-col space-y-2 justify-center w-full items-center', className)} {...rest}>
      <div className='bg-background rounded-lg shadow-md w-full container z-[2]'>
        <Heading title='Clone Content' />

        <form onSubmit={handleSubmit(onSubmit)} className='p-6 md:p-10'>
          <Alert
            message='Please read below before proceeding!'
            type='warn'
            className='mb-10'
            list={[
              'Currently only questions can be cloned along with associated data like picture, choice etc.',
              'The cloned data will be <b>visible immediately</b> and will have <b>createdAt</b> timestamp of the source data!',
              'In case of error, nothing will be cloned',
            ]}
          />
          <div className='grid grid-cols-3 items-start gap-y-6 md:gap-y-10 gap-x-8'>
            <FormSelect
              isRequired={true}
              description='Carefully select this!'
              options={batchTypes.filter((b) => b.key === EBatchType.QUESTION)}
              control={control}
              transform={(a) => Number.parseInt(a, 10)}
              labelPlacement='outside'
              name='type'
              placeholder='Select Type'
              className='rounded col-span-3 md:col-span-1'
              variant='faded'
              label='Type'
            />
            <FormSelect
              isRequired={true}
              options={dbTypes}
              description='Carefully select the database you want to clone from'
              control={control}
              onValueChange={handleSourceChange}
              transform={(a) => Number.parseInt(a, 10)}
              labelPlacement='outside'
              placeholder='Select Source Database'
              name='source'
              className='rounded col-span-3 md:col-span-1'
              variant='faded'
              label='Source DB'
            />
            <FormSelect
              isRequired={true}
              options={destinationTypes}
              description='Carefully select the database you want to clone to'
              control={control}
              transform={(a) => Number.parseInt(a, 10)}
              labelPlacement='outside'
              placeholder='Select Destination Database'
              name='destination'
              className='rounded col-span-3 md:col-span-1'
              variant='faded'
              label='Destination DB'
            />
            <FormFileInput
              isRequired={true}
              description='Make sure that zip folder contains only final.csv'
              className='col-span-3 h-full'
              control={control}
              name='file'
              label='Zip File'
            />
          </div>
          <div className='flex justify-center mt-10'>
            <Button
              isDisabled={!isValid}
              isLoading={loading}
              className='w-full max-w-xl bg-primary'
              type='submit'
              radius='sm'
              size='lg'
            >
              Submit
            </Button>
          </div>
        </form>
      </div>
      <AnimatedComponent>
        {finalData && (
          <motion.div
            initial={{ y: -160 }}
            animate={{ y: 0 }}
            exit={{ y: -160 }}
            transition={{ type: 'spring' }}
            className={cn(
              'flex flex-col space-y-4 justify-center items-center',
              'w-full container py-5',
              'bg-background rounded-lg shadow-md ',
            )}
          >
            {finalData?.status === 200 ? (
              <SuccessDataFeedback message={finalData?.message} response={finalData} />
            ) : (
              <ErrorFeedback response={finalData} />
            )}
          </motion.div>
        )}
      </AnimatedComponent>
    </div>
  );
};

const CloneTemplate = ({ className, ...rest }: React.ComponentProps<'div'>) => {
  const schema = z.object({
    type: z.enum(EBatchType, { message: 'Invalid Product value' }),
  });

  type ISchema = z.infer<typeof schema>;
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<ISchema>({
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  const onSubmit: SubmitHandler<ISchema> = async (input) => {
    let filePath = '';

    switch (input.type) {
      case EBatchType.QUESTION:
        filePath = '/templates/clone/question.zip';
        break;

      default:
        break;
    }

    if (!filePath) {
      toaster.info('We dont have template for this content yet!');
      return;
    }
    localFileDownload(`CLONE_TEMPLATE_${EBatchType[input.type]}.zip`, filePath);
  };

  return (
    <div className={cn('flex flex-col space-y-2 justify-center w-full items-center', className)} {...rest}>
      <div className='bg-background rounded-lg shadow-md w-full container z-[2]'>
        <Heading title='Download Template Folder' />
        <form onSubmit={handleSubmit(onSubmit)} className='p-6 md:p-10'>
          <FormSelect
            isRequired={true}
            description=''
            options={batchTypes.filter((b) => b.key === EBatchType.QUESTION)}
            control={control}
            transform={(a) => Number.parseInt(a, 10)}
            labelPlacement='inside'
            name='type'
            placeholder='Select Type'
            className='rounded col-span-3 md:col-span-1'
            variant='faded'
            label='Type'
          />

          <div className='flex justify-center mt-10'>
            <Button
              isDisabled={!isValid}
              className='w-full max-w-xl bg-primary'
              type='submit'
              radius='sm'
              size='lg'
            >
              Download
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Clone = () => {
  return (
    <div className='flex w-full justify-center h-full'>
      <div className='grid grid-cols-4 w-full items-start gap-8 container'>
        <CloneController className='col-span-4 xl:col-span-3 ' />
        <CloneTemplate className='col-span-4 xl:col-span-1 ' />
      </div>
    </div>
  );
};

export default Clone;
